/**
 * Nível 2 of the nav refactor — multi-select post-type filter chips shown
 * inside the Local/Following feed (and the group feed). No "All" chip:
 * nothing selected already means "show everything" (see
 * SPEC_nav_menu_refactor.md). Sticky, like the Nível 1 bar above it —
 * sticks to the top of the scrolling feed container (#feed-scroll in
 * Layout.tsx), not the viewport, since FeedBar itself sits outside that
 * scroll container and doesn't need position:sticky to stay put.
 */
import { useEffect, useRef, useState } from 'react'
import { FileText, Calendar, Gift, LifeBuoy, Zap } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { FeedTypeChip } from '@/store/feed.store'

const CHIPS: Array<{ value: FeedTypeChip; labelKey: string; Icon: typeof FileText }> = [
  { value: 'post', labelKey: 'post.post_type_post', Icon: FileText },
  { value: 'action', labelKey: 'feed.type_action', Icon: Zap },
  { value: 'help', labelKey: 'post.post_type_help', Icon: LifeBuoy },
  { value: 'event', labelKey: 'post.post_type_event', Icon: Calendar },
  { value: 'offer', labelKey: 'post.post_type_offer', Icon: Gift },
]

const EDGE_FADE = 20

interface FeedTypeChipsProps {
  selectedTypes: FeedTypeChip[]
  onToggle: (type: FeedTypeChip) => void
}

// Controlled component — callers own the filter state. Home wires it to the
// persisted global feed store; a group feed gets its own local, non-persisted
// state instead, since a filter chosen in one group (or in Home) shouldn't
// leak into another feed surface when you navigate away.
export function FeedTypeChips({ selectedTypes, onToggle }: FeedTypeChipsProps) {
  const { t } = useTranslation()
  const scrollRef = useRef<HTMLDivElement>(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  // Edge fade is driven by actual scroll position (not just "does it
  // overflow") so it disappears once the user has scrolled all the way to
  // that side — it's a "there's more this way" hint, not decoration.
  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    function update() {
      setCanScrollLeft(el!.scrollLeft > 2)
      setCanScrollRight(el!.scrollLeft + el!.clientWidth < el!.scrollWidth - 2)
    }
    update()
    el.addEventListener('scroll', update, { passive: true })
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => {
      el.removeEventListener('scroll', update)
      ro.disconnect()
    }
  }, [selectedTypes])

  // mask-image (not an overlay) so the fade works over whatever background
  // sits behind the chips (Home vs. group page, light vs. dark) without
  // needing to know/match that color.
  const maskImage = !canScrollLeft && !canScrollRight
    ? undefined
    : `linear-gradient(to right, ${canScrollLeft ? 'transparent 0, black ' + EDGE_FADE + 'px' : 'black 0'}, black calc(100% - ${canScrollRight ? EDGE_FADE : 0}px), ${canScrollRight ? 'transparent 100%' : 'black 100%'})`

  // overflowX lives on this single element (not a centered wrapper around a
  // scrolling child) on purpose: a flex container that is both
  // justify-content: center AND overflow-x: auto clips the start of an
  // overflowing line with no way to scroll back to it (browsers computed
  // the centered line's scrollable region symmetrically, so scrollLeft: 0
  // already sits past the first item) — that's why there's no
  // justifyContent here.
  return (
    <div
      ref={scrollRef}
      style={{
        display: 'flex',
        gap: 6,
        overflowX: 'auto',
        padding: '8px 12px',
        flexShrink: 0,
        position: 'sticky',
        top: 0,
        zIndex: 'var(--z-header)',
        background: 'var(--bg-sunk)',
        borderBottom: '1px solid var(--border)',
        WebkitMaskImage: maskImage,
        maskImage,
      }}
    >
      {CHIPS.map(({ value, labelKey, Icon }) => {
        const active = selectedTypes.includes(value)
        return (
          <button
            key={value}
            type="button"
            onClick={() => onToggle(value)}
            aria-pressed={active}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 5,
              padding: '5px 12px',
              border: '1.5px solid',
              borderColor: active ? 'var(--brand)' : 'var(--border)',
              borderRadius: 'var(--radius-pill)',
              background: active ? 'var(--brand)' : 'transparent',
              color: active ? 'var(--on-brand)' : 'var(--text-body)',
              fontFamily: 'var(--font-sans)',
              fontWeight: 700,
              fontSize: 'var(--fs-xs)',
              cursor: 'pointer',
              flexShrink: 0,
              whiteSpace: 'nowrap',
            }}
          >
            <Icon size={13} />
            {t(labelKey)}
          </button>
        )
      })}
    </div>
  )
}
